import {
  Component,
  ElementRef,
  HostListener,
  OnInit,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';

import { FocusTrapDirective } from '../../../shared/directives/focus-trap/focus-trap.directive';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { AdminUserFilter } from '../../models/admin-user-filter';
import {
  minLengthError,
  normalizeCriteria,
} from '../../../shared/utils/search-field/search-field.utils';

/**
 * "Search by User / Employee" modal. Collects the admin/staff user's first/last name and emits
 * them as an {@link AdminUserFilter} so the audit log can narrow to that user's admin actions.
 * (The API's admin-user branch matches on name only; there is no department filter.)
 */
@Component({
  selector: 'app-user-search-modal',
  standalone: true,
  imports: [IconComponent, FocusTrapDirective],
  templateUrl: './user-search-modal.html',
  styleUrl: './user-search-modal.scss',
})
export class UserSearchModal implements OnInit {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  /** The currently-applied filter, used to prefill the fields when the modal reopens. */
  readonly initialFilter = input<AdminUserFilter | null>(null);

  readonly applied = output<AdminUserFilter>();
  readonly cleared = output<void>();
  readonly closed = output<void>();

  readonly firstName = signal('');
  readonly lastName = signal('');

  /**
   * Per-field validation errors, shown inline and used to gate the search. A blank field is
   * omitted (not part of the query); a populated field must clear the shared minimum length —
   * see {@link minLengthError}.
   */
  readonly firstNameError = computed(() => minLengthError(this.firstName(), 'First name'));
  readonly lastNameError = computed(() => minLengthError(this.lastName(), 'Last name'));

  /**
   * Per-field "touched" flags, flipped on blur (or a failed search), so inline errors only render
   * once the user moves on from a field rather than on every keystroke.
   */
  readonly firstNameTouched = signal(false);
  readonly lastNameTouched = signal(false);

  /** True once at least one field has content — the point of a "Search by User" query. */
  private readonly hasAnyField = computed(
    () => this.firstName().trim().length > 0 || this.lastName().trim().length > 0,
  );

  /** True when at least one field is populated and every populated field passes validation. */
  readonly canSearch = computed(
    () => this.hasAnyField() && this.firstNameError() === null && this.lastNameError() === null,
  );

  ngOnInit(): void {
    const filter = this.initialFilter();
    if (filter) {
      this.firstName.set(filter.firstName ?? '');
      this.lastName.set(filter.lastName ?? '');
    }

    setTimeout(() => {
      this.elementRef.nativeElement.querySelector<HTMLElement>('input')?.focus();
    });
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.close();
  }

  close(): void {
    this.closed.emit();
  }

  /**
   * Reset the fields and drop the applied filter. The modal doesn't dismiss itself — it emits
   * `cleared` and the parent decides; today `ActivityLogs.clearUserFilter()` closes it.
   */
  clear(): void {
    this.firstName.set('');
    this.lastName.set('');
    this.firstNameTouched.set(false);
    this.lastNameTouched.set(false);
    this.cleared.emit();
  }

  search(): void {
    if (!this.canSearch()) {
      // Reveal any outstanding errors when the user tries to search (e.g. via Enter) so a blocked
      // search isn't a silent dead-end.
      this.firstNameTouched.set(true);
      this.lastNameTouched.set(true);
      return;
    }

    this.applied.emit({
      firstName: normalizeCriteria(this.firstName()),
      lastName: normalizeCriteria(this.lastName()),
    });
  }
}
